const NumpyJson = [
  {
    "id": 1,
    "html_title": "intro",
    "bootstrap_head_desc": [
      {
        "bootstrap_title_heading": "What is NumPy?",
        "bootstrap_title_desc": `NumPy stands for Numerical Python. It is a powerful Python library used for performing mathematical and scientific computations.
  
  It is mainly used for:
  - Handling arrays and matrices (multi-dimensional data)
  - Performing numerical operations efficiently
  - Supporting data science, machine learning, and scientific analysis`
      },
      {
        "bootstrap_title_heading": "Why Use NumPy?",
        "bootstrap_title_desc": `Python lists are flexible but slow when it comes to numerical tasks. NumPy arrays are optimized for speed and memory usage.
  
  Main advantages of NumPy:
  - Faster mathematical operations
  - Less memory usage
  - Built-in functions for statistics, algebra, and more
  - Used in data science, AI, machine learning, and big data applications`
      },
      {
        "bootstrap_title_heading": "Installing NumPy",
        "bootstrap_title_desc": `Before using NumPy, it must be installed on your system.
  
  Open your terminal or command prompt and run:
  
  pip install numpy
  
  This command installs the NumPy library so it can be used in your Python programs.`
      },
      {
        "bootstrap_title_heading": "Importing NumPy",
        "bootstrap_title_desc": `Once NumPy is installed, you can use it in your Python code by importing it:
  
  import numpy as np
  
  Here, "np" is a commonly used alias that makes the code shorter and easier to read. You can now use functions like np.array(), np.mean(), np.sum(), etc.`
      },
      {
        "bootstrap_title_heading": "Creating Your First Array",
        "bootstrap_title_desc": `A NumPy array is like a list, but much more powerful and efficient for numerical tasks.
  
  Example:
  
  import numpy as np  
  a = np.array([10, 20, 30, 40])  
  print(a)
  
  Output: [10 20 30 40]
  
  This is a one-dimensional (1D) array.
  
  To create a two-dimensional (2D) array (like a table):
  
  b = np.array([[1, 2], [3, 4]])  
  print(b)
  
  Output:
  [[1 2]  
   [3 4]]`
      }
    ],
    "coursename": "Intro to NumPy"
  }
,  


{
  "id": 2,
  "html_title": "array-basics",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Understanding NumPy Arrays",
      "bootstrap_title_desc": `NumPy arrays are similar to Python lists, but with key differences:
- All elements in a NumPy array must be of the same type (e.g., all integers or all floats)
- Arrays are more efficient in terms of memory and speed
- Arrays support vectorized operations (performing actions on entire arrays at once)

Example:
import numpy as np  
arr = np.array([10, 20, 30])  
print(arr.dtype)   # Shows the data type  
print(arr.shape)   # Shows the shape (number of elements,)`
    },
    {
      "bootstrap_title_heading": "Types of Arrays",
      "bootstrap_title_desc": `NumPy supports different types of arrays based on the number of dimensions:

1D Array: A single list of values  
2D Array: A table (rows and columns)  
3D Array: A group of 2D tables

Examples:
1D: np.array([1, 2, 3])  
2D: np.array([[1, 2], [3, 4]])  
3D: np.array([[[1, 2], [3, 4]], [[5, 6], [7, 8]]])`
    },
    {
      "bootstrap_title_heading": "Array Attributes",
      "bootstrap_title_desc": `You can inspect arrays using built-in attributes:

- .ndim → Number of dimensions  
- .shape → Shape of the array (rows, columns)  
- .size → Total number of elements  
- .dtype → Data type of elements

Example:
a = np.array([[1, 2, 3], [4, 5, 6]])  
print(a.ndim)   # 2  
print(a.shape)  # (2, 3)  
print(a.size)   # 6  
print(a.dtype)  # int64 (or similar)`
    },
    {
      "bootstrap_title_heading": "Creating Special Arrays",
      "bootstrap_title_desc": `NumPy provides functions to create arrays quickly:

np.zeros((2, 3)) → 2x3 array filled with zeros  
np.ones((3, 2)) → 3x2 array filled with ones  
np.eye(3) → 3x3 identity matrix  
np.arange(0, 10, 2) → Array: [0 2 4 6 8]  
np.linspace(0, 1, 5) → 5 evenly spaced values from 0 to 1`
    },
    {
      "bootstrap_title_heading": "Array Data Types",
      "bootstrap_title_desc": `You can specify the data type while creating an array.

Supported types include:
- int → Whole numbers  
- float → Decimal numbers  
- bool → True/False  
- str → Text values

Example:
arr = np.array([1, 2, 3], dtype='float')  
print(arr)       # [1. 2. 3.]  
print(arr.dtype) # float64`
    }
  ],
  "coursename": "NumPy Arrays"
},


{
  "id": 3,
  "html_title": "indexing-slicing-modification",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Accessing Elements (Indexing)",
      "bootstrap_title_desc": `Indexing is how we get specific values from an array. NumPy uses 0-based indexing (first element is index 0).

→ 1D Array Example:
arr = np.array([10, 20, 30, 40, 50])
print(arr[0])     → 10  (first element)
print(arr[3])     → 40  (fourth element)
print(arr[-1])    → 50  (last element using negative index)

→ 2D Array Example:
arr2d = np.array([[1, 2, 3],
                  [4, 5, 6],
                  [7, 8, 9]])
print(arr2d[0, 1]) → 2   (row 0, column 1)
print(arr2d[2, 2]) → 9   (last row, last column)`
    },
    {
      "bootstrap_title_heading": "Slicing Arrays",
      "bootstrap_title_desc": `Slicing lets you select a part of an array (a range of elements).

Syntax: array[start:stop:step] (stop is excluded)

→ 1D Array Slicing:
arr = np.array([10, 20, 30, 40, 50, 60])
print(arr[1:4])    → [20 30 40]
print(arr[:3])     → [10 20 30]
print(arr[::2])    → [10 30 50]
print(arr[::-1])   → [60 50 40 30 20 10]  (reversed)

→ 2D Array Slicing:
arr2d = np.array([[11, 12, 13],
                  [21, 22, 23],
                  [31, 32, 33]])

print(arr2d[:2, 1:])  → [[12 13], [22 23]] (first 2 rows, last 2 columns)
print(arr2d[1:, :2])  → [[21 22], [31 32]] (last 2 rows, first 2 columns)`
    },
    {
      "bootstrap_title_heading": "Modifying Array Elements",
      "bootstrap_title_desc": `You can update values in arrays using indexing or slicing.

→ Change 1 Element in 1D:
arr = np.array([1, 2, 3, 4, 5])
arr[2] = 99
print(arr) → [1 2 99 4 5]

→ Change Multiple Elements:
arr[1:4] = [10, 20, 30]
print(arr) → [1 10 20 30 5]

→ Modify in 2D Array:
arr2d = np.array([[1, 2], [3, 4]])
arr2d[0, 1] = 99
print(arr2d) → [[1 99], [3 4]]`
    },
    {
      "bootstrap_title_heading": "Practice: Modify a 2D Array",
      "bootstrap_title_desc": `Task:
1. Create a 2D array: [[10, 20, 30], [40, 50, 60]]
2. Change the first row, second element to 999
3. Replace the entire second row with [100, 200, 300]

→ Code:
arr = np.array([[10, 20, 30],
                [40, 50, 60]])

arr[0, 1] = 999
arr[1] = [100, 200, 300]

print(arr)

→ Output:
[[ 10 999  30]
 [100 200 300]]`
    }
  ],
  "coursename": "Indexing, Slicing"
}
,

{
  "id": 4,
  "html_title": "mathematical-operations",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Basic Arithmetic on Arrays",
      "bootstrap_title_desc": `NumPy allows you to do arithmetic directly on arrays without using loops.

→ Example:
a = np.array([1, 2, 3])
b = np.array([4, 5, 6])

print(a + b)   → [5 7 9]
print(a - b)   → [-3 -3 -3]
print(a * b)   → [4 10 18]
print(b / a)   → [4.  2.5 2.]

→ Operations with scalars:
print(a + 10)  → [11 12 13]
print(b * 2)   → [8 10 12]`
    },
    {
      "bootstrap_title_heading": "Mathematical Functions",
      "bootstrap_title_desc": `NumPy has built-in functions to apply mathematical operations on arrays.

→ Example:
arr = np.array([1, 4, 9, 16])

print(np.sqrt(arr))   → [1. 2. 3. 4.]
print(np.exp(arr))    → [2.718... 54.598... 8103... 8.8e6...]
print(np.log(arr))    → [0.         1.38629436 2.19722458 2.77258872]`
    },
    {
      "bootstrap_title_heading": "Aggregate Functions (Sum, Min, Max, Mean)",
      "bootstrap_title_desc": `You can easily summarize array data:

arr = np.array([10, 20, 30, 40])

→ print(np.sum(arr))   → 100
→ print(np.min(arr))   → 10
→ print(np.max(arr))   → 40
→ print(np.mean(arr))  → 25.0
→ print(np.std(arr))   → 11.18 (standard deviation)`
    },
    {
      "bootstrap_title_heading": "Operations on 2D Arrays (Axis)",
      "bootstrap_title_desc": `In 2D arrays, use **axis** to specify direction:

→ axis=0 → operate column-wise  
→ axis=1 → operate row-wise

arr2d = np.array([[1, 2, 3],
                  [4, 5, 6]])

→ print(np.sum(arr2d))            → 21  (total sum)
→ print(np.sum(arr2d, axis=0))    → [5 7 9]   (sum of each column)
→ print(np.sum(arr2d, axis=1))    → [6 15]    (sum of each row)

→ print(np.mean(arr2d, axis=1))   → [2. 5.]`
    },
    {
      "bootstrap_title_heading": "Practice Task",
      "bootstrap_title_desc": `Task:
1. Create an array: [2, 4, 6, 8, 10]
2. Multiply all elements by 3
3. Find the average, sum, and standard deviation

→ Code:
arr = np.array([2, 4, 6, 8, 10])
arr = arr * 3
print("New Array →", arr)
print("Sum →", np.sum(arr))
print("Mean →", np.mean(arr))
print("Std Dev →", np.std(arr))

→ Output:
New Array → [ 6 12 18 24 30]
Sum → 90
Mean → 18.0
Std Dev → 8.485`
    }
  ],
  "coursename": "Maths Operations"
},

{
  "id": 5,
  "html_title": "broadcasting-ufuncs",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Broadcasting?",
      "bootstrap_title_desc": `Broadcasting means applying operations between arrays of different shapes.

→ Example:
arr = np.array([1, 2, 3])
print(arr + 10)   → [11 12 13]

→ Here, 10 is automatically stretched to match the shape of arr.

→ Example:
a = np.array([[1, 2, 3],
              [4, 5, 6]])

b = np.array([10, 20, 30])

print(a + b)

→ Output:
[[11 22 33]
 [14 25 36]]

→ b (1D array) is broadcast across rows of a (2D array).`
    },
    {
      "bootstrap_title_heading": "Why Broadcasting is Useful",
      "bootstrap_title_desc": `→ Allows you to write less code  
→ No need for loops  
→ Faster performance

Example:
a = np.array([1, 2, 3])
b = 2
print(a * b) → [2 4 6]`
    },
    {
      "bootstrap_title_heading": "Universal Functions (ufuncs)",
      "bootstrap_title_desc": `uFuncs are NumPy functions that operate element-wise.

→ Example:
arr = np.array([1, 4, 9])

print(np.sqrt(arr))  → [1. 2. 3.]
print(np.square(arr))→ [1 16 81]
print(np.exp(arr))   → [2.718... 54.598... 8103...]

→ Trigonometric:
angles = np.array([0, np.pi/2, np.pi])
print(np.sin(angles)) → [0. 1. 0.]`
    },
    {
      "bootstrap_title_heading": "Using ufuncs with Two Arrays",
      "bootstrap_title_desc": `→ Example:
a = np.array([1, 2, 3])
b = np.array([4, 5, 6])

print(np.add(a, b))   → [5 7 9]
print(np.subtract(a, b)) → [-3 -3 -3]
print(np.multiply(a, b)) → [4 10 18]
print(np.divide(b, a))   → [4. 2.5 2.]`
    },
    {
      "bootstrap_title_heading": "Practice Task",
      "bootstrap_title_desc": `Task:
1. Create array a = [1, 2, 3, 4]
2. Add 5 to each element (broadcasting)
3. Calculate square and square root (ufuncs)

→ Code:
a = np.array([1, 2, 3, 4])
a_plus = a + 5
print("After adding 5 →", a_plus)
print("Square →", np.square(a_plus))
print("Square root →", np.sqrt(a_plus))

→ Output:
After adding 5 → [6 7 8 9]
Square → [36 49 64 81]
Square root → [2.449 2.645 2.828 3.000]`
    }
  ],
  "coursename": "Broadcasting"
}
,

{
  "id": 6,
  "html_title": "conditional-selection-boolean-indexing",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Conditional Selection?",
      "bootstrap_title_desc": `Conditional selection lets you pick elements from an array based on conditions.

→ Example:
arr = np.array([10, 20, 30, 40, 50])
print(arr > 25) → [False False True True True]

→ We can use this to filter:
print(arr[arr > 25]) → [30 40 50]`
    },
    {
      "bootstrap_title_heading": "Boolean Indexing",
      "bootstrap_title_desc": `Boolean indexing means using True/False values to select data.

→ Example:
arr = np.array([5, 10, 15, 20, 25])
condition = arr % 2 == 0
print(condition) → [False True False True False]

→ Filter even numbers:
print(arr[condition]) → [10 20]`
    },
    {
      "bootstrap_title_heading": "Combining Multiple Conditions",
      "bootstrap_title_desc": `We can combine conditions using:
→ & (AND)
→ | (OR)
→ ~ (NOT)

→ Example:
arr = np.array([10, 20, 30, 40, 50])
print(arr[(arr > 15) & (arr < 45)]) → [20 30 40]

→ Example:
print(arr[(arr == 10) | (arr == 50)]) → [10 50]`
    },
    {
      "bootstrap_title_heading": "Modifying with Conditions",
      "bootstrap_title_desc": `We can also change values using conditions.

→ Example:
arr = np.array([1, 2, 3, 4, 5])
arr[arr < 3] = 0
print(arr) → [0 0 3 4 5]`
    },
    {
      "bootstrap_title_heading": "Practice Task",
      "bootstrap_title_desc": `Task:
1. Create arr = [10, 15, 20, 25, 30]
2. Select values greater than 18
3. Replace values less than 18 with -1

→ Code:
arr = np.array([10, 15, 20, 25, 30])
print("Greater than 18 →", arr[arr > 18])
arr[arr < 18] = -1
print("After replacing →", arr)

→ Output:
Greater than 18 → [20 25 30]
After replacing → [-1 -1 20 25 30]`
    }
  ],
  "coursename": "Conditional Selection"
},


{
  "id": 7,
  "html_title": "working-with-csv",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Reading Data from CSV",
      "bootstrap_title_desc": `We can read real-world datasets (like Excel or CSV files) using NumPy.

→ Example:
data = np.genfromtxt('data.csv', delimiter=',', skip_header=1)

→ 'data.csv' → name of the file  
→ delimiter=',' → separates values by comma  
→ skip_header=1 → skips the first row (header)

This gives you a NumPy array from the CSV file.`
    },
    {
      "bootstrap_title_heading": "Simple Example CSV",
      "bootstrap_title_desc": `Suppose 'scores.csv' contains:

name,math,english  
John,85,78  
Jane,92,88  
Mike,75,82

→ Code:
data = np.genfromtxt('scores.csv', delimiter=',', skip_header=1, usecols=(1,2))
print(data)

→ Output:
[[85. 78.]
 [92. 88.]
 [75. 82.]]`
    },
    {
      "bootstrap_title_heading": "Analyzing CSV Data",
      "bootstrap_title_desc": `Once loaded, you can calculate:

→ Average:
print(np.mean(data, axis=0)) → [84.   82.6667]

→ Maximum:
print(np.max(data, axis=0)) → [92. 88.]

→ Minimum:
print(np.min(data, axis=0)) → [75. 78.]`
    },
    {
      "bootstrap_title_heading": "Saving Arrays to CSV",
      "bootstrap_title_desc": `We can save NumPy arrays to a CSV file.

→ Example:
arr = np.array([[1, 2, 3], [4, 5, 6]])
np.savetxt('output.csv', arr, delimiter=',', fmt='%d')

→ 'output.csv' → output file name  
→ fmt='%d' → format as integer`
    },
    {
      "bootstrap_title_heading": "Practice Task",
      "bootstrap_title_desc": `Task:
1. Load 'sales.csv' with columns: product, price, quantity  
2. Calculate average price and total quantity  
3. Save the result to 'summary.csv'

→ Code:
data = np.genfromtxt('sales.csv', delimiter=',', skip_header=1, usecols=(1,2))
avg_price = np.mean(data[:,0])
total_qty = np.sum(data[:,1])
summary = np.array([[avg_price, total_qty]])
np.savetxt('summary.csv', summary, delimiter=',', fmt='%.2f')`
    }
  ],
  "coursename": "CSV files"
},


{
  "id": 8,
  "html_title": "reshaping-flattening-stacking",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Reshaping Arrays",
      "bootstrap_title_desc": `We can change the shape of an array without changing its data.

→ Example:
a = np.array([1, 2, 3, 4, 5, 6])
b = a.reshape((2, 3))
print(b)

→ Output:
[[1 2 3]
 [4 5 6]]

→ Note:
- The total number of elements must match.
- Use -1 to let NumPy figure it out: a.reshape((3, -1))`
    },
    {
      "bootstrap_title_heading": "Flattening Arrays",
      "bootstrap_title_desc": `Flatten turns a multi-dimensional array into a 1D array.

→ Example:
a = np.array([[1, 2], [3, 4]])
b = a.flatten()
print(b)

→ Output:
[1 2 3 4]

→ Another way:
b = a.ravel() → returns a flattened view (faster, but linked to original)`
    },
    {
      "bootstrap_title_heading": "Stacking Arrays Vertically",
      "bootstrap_title_desc": `You can stack arrays on top of each other.

→ Example:
a = np.array([1, 2])
b = np.array([3, 4])
c = np.vstack((a, b))
print(c)

→ Output:
[[1 2]
 [3 4]]`
    },
    {
      "bootstrap_title_heading": "Stacking Arrays Horizontally",
      "bootstrap_title_desc": `You can stack arrays side by side.

→ Example:
a = np.array([[1], [2]])
b = np.array([[3], [4]])
c = np.hstack((a, b))
print(c)

→ Output:
[[1 3]
 [2 4]]`
    },
    {
      "bootstrap_title_heading": "Practice Task",
      "bootstrap_title_desc": `Task:
1. Create a 1D array from 1 to 12.  
2. Reshape it to 3x4.  
3. Flatten it back to 1D.  
4. Stack two 1D arrays vertically and horizontally.

→ Code:
arr = np.arange(1, 13)
reshaped = arr.reshape((3, 4))
flattened = reshaped.flatten()
a = np.array([1, 2])
b = np.array([3, 4])
v_stack = np.vstack((a, b))
h_stack = np.hstack((a, b))`
    }
  ],
  "coursename": "Reshaping, Flattening, and Stacking"
},

{
  "id": 9,
  "html_title": "random-numbers-simulations",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Generating Random Numbers",
      "bootstrap_title_desc": `NumPy can generate random numbers for simulations, testing, and modeling.

→ Example:
np.random.rand(3)

→ Output:
[0.45 0.67 0.12]  # 3 random numbers between 0 and 1`
    },
    {
      "bootstrap_title_heading": "Random Integers",
      "bootstrap_title_desc": `Generate random integers between two values.

→ Example:
np.random.randint(1, 10, size=5)

→ Output:
[3 7 1 9 4]  # 5 random integers between 1 and 9`
    },
    {
      "bootstrap_title_heading": "Random Choice",
      "bootstrap_title_desc": `Pick random elements from an array.

→ Example:
arr = np.array([10, 20, 30, 40])
choice = np.random.choice(arr, size=2)
print(choice)

→ Output:
[20 40]  # two random picks from the array`
    },
    {
      "bootstrap_title_heading": "Random Seed",
      "bootstrap_title_desc": `A seed makes random numbers predictable (useful for testing).

→ Example:
np.random.seed(42)
print(np.random.rand(2))

→ Output (always same with seed 42):
[0.37454012 0.95071431]`
    },
    {
      "bootstrap_title_heading": "Simulating Data (Practical Example)",
      "bootstrap_title_desc": `→ Example: Simulate 10 dice rolls

rolls = np.random.randint(1, 7, size=10)
print(rolls)

→ Output:
[3 6 1 5 2 4 6 2 3 5]

→ Example: Simulate 100 coin flips (0 = heads, 1 = tails)
flips = np.random.randint(0, 2, size=100)
heads_count = np.sum(flips == 0)
tails_count = np.sum(flips == 1)
print(f"Heads: {heads_count}, Tails: {tails_count}")`
    }
  ],
  "coursename": "Random Numbers and Simulations"
}
,

{
  "id": 10,
  "html_title": "mini-project-final-tasks",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Mini Project: Data Analysis with NumPy",
      "bootstrap_title_desc": `→ Step 1: Generate random student marks
marks = np.random.randint(0, 101, size=30)
print(marks)

→ Step 2: Calculate average, max, min
average = np.mean(marks)
max_score = np.max(marks)
min_score = np.min(marks)
print(f"Average: {average}, Max: {max_score}, Min: {min_score}")`
    },
    {
      "bootstrap_title_heading": "Task 1: Find Top 5 Scores",
      "bootstrap_title_desc": `→ Example:
top_5 = np.sort(marks)[-5:]
print(f"Top 5 scores: {top_5}")`
    },
    {
      "bootstrap_title_heading": "Task 2: Count Students Scoring Above 50",
      "bootstrap_title_desc": `→ Example:
count_above_50 = np.sum(marks > 50)
print(f"Students scoring above 50: {count_above_50}")`
    },
    {
      "bootstrap_title_heading": "Task 3: Reshape and Analyze Data",
      "bootstrap_title_desc": `→ Example:
reshaped = marks.reshape(6, 5)  # 6 rows, 5 columns
print(reshaped)

→ Calculate row-wise average:
row_avg = np.mean(reshaped, axis=1)
print(f"Row averages: {row_avg}")`
    },
    {
      "bootstrap_title_heading": "Task 4: Simulate 1000 Coin Flips",
      "bootstrap_title_desc": `→ Example:
flips = np.random.randint(0, 2, size=1000)
heads = np.sum(flips == 0)
tails = np.sum(flips == 1)
print(f"Heads: {heads}, Tails: {tails}")`
    }
  ],
  "coursename": "Mini Project & Final Practice"
},












      
      
      
      
      
      
  ];
  export default NumpyJson;
  